import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Textarea, LoadingButton } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { getTaskActivities, addTaskComment } from '../services/api';
import { useMembers, memberName } from '../stores/lookupStore';
import { PMTaskActivity } from '../types/pm';

/**
 * 任務的活動記錄與留言（編輯任務時顯示在表單下方）
 * 系統事件一行小字（誰改了什麼），留言顯示內容；新留言會通知負責人與之前留過言的人
 */
const TaskActivityPanel: React.FC<{ taskId: string }> = ({ taskId }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const members = useMembers();
  const [activities, setActivities] = useState<PMTaskActivity[]>([]);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTaskActivities(taskId)
      .then(({ data }) => !cancelled && setActivities(data))
      .catch(error => console.error('Failed to load activities:', error));
    return () => { cancelled = true; };
  }, [taskId]);

  const name = (id?: string | null) => memberName(members, id) || t('pmTasks.unassigned', { ns: 'pages' });
  const describe = (a: PMTaskActivity) => {
    const actor = name(a.userId);
    switch (a.type) {
      case 'CREATED': return t('pmTasks.activity.created', { ns: 'pages', actor });
      case 'STATUS': return t('pmTasks.activity.status', { ns: 'pages', actor, value: t(`pmTasks.status.${a.toValue}`, { ns: 'pages' }) });
      case 'ASSIGNEE': return t('pmTasks.activity.assignee', { ns: 'pages', actor, value: name(a.toValue) });
      case 'DUE_DATE': return t('pmTasks.activity.dueDate', { ns: 'pages', actor, value: a.toValue ? formatTableDate(a.toValue, i18n.language) : '—' });
      default: return '';
    }
  };

  const send = async () => {
    const body = comment.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await addTaskComment(taskId, body);
      setActivities(prev => [...prev, data]);
      setComment('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="border-t border-line pt-5">
      <h3 className="text-sm font-semibold text-fg mb-3">{t('pmTasks.activity.title', { ns: 'pages' })}</h3>
      <ol className="space-y-3 mb-4">
        {activities.map(a => (
          <li key={a.id} className="text-sm">
            {a.type === 'COMMENT' ? (
              <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 px-3 py-2">
                <div className="flex items-baseline justify-between gap-2 text-[13px]">
                  <span className="font-medium text-fg">{name(a.userId)}</span>
                  <span className="text-fg-muted tabular-nums">{formatTableDate(a.createdAt, i18n.language)}</span>
                </div>
                <p className="mt-1 text-fg whitespace-pre-wrap break-words">{a.body}</p>
              </div>
            ) : (
              <p className="text-[13px] text-fg-secondary">
                {describe(a)}
                <span className="ml-2 text-fg-muted tabular-nums">{formatTableDate(a.createdAt, i18n.language)}</span>
              </p>
            )}
          </li>
        ))}
      </ol>
      <Textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        onKeyDown={e => {
          // Ctrl／⌘＋Enter 送出留言（不送出任務表單）
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            send();
          }
        }}
        rows={2}
        maxLength={2000}
        placeholder={t('pmTasks.activity.placeholder', { ns: 'pages' })}
      />
      <div className="mt-2 flex justify-end">
        <LoadingButton type="button" onClick={send} loading={sending} disabled={!comment.trim()}>
          {t('pmTasks.activity.send', { ns: 'pages' })}
        </LoadingButton>
      </div>
    </section>
  );
};

export default TaskActivityPanel;
